/** Reusable sortable race results table */
import { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useI18n } from '@/context/I18nContext';
import { PositionBadge } from './PositionBadge';
import { StatusBadge } from './StatusBadge';
import type { RaceEntryResult } from '@/types/telemetry';

interface ResultsTableProps {
  entries: RaceEntryResult[];
  compact?: boolean;
  showLaps?: boolean;
  highlightDriverId?: string;
  highlightDriverIds?: ReadonlySet<string>;
  onDriverClick?: (driverId: string) => void;
}

type SortField = 'finishPosition' | 'kartNumber' | 'bestLapMs' | 'totalTimeMs' | 'gridPosition' | 'lapsCompleted';
type SortDir = 'asc' | 'desc';

function formatLapTime(ms: number): string {
  if (!ms || ms <= 0) return '–';
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = ms % 1000;
  if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${millis.toString().padStart(3, '0')}`;
  }
  return `${seconds}.${millis.toString().padStart(3, '0')}`;
}

export { formatLapTime };

export function ResultsTable({ entries, compact = false, showLaps = false, highlightDriverId, highlightDriverIds, onDriverClick }: ResultsTableProps) {
  const { t } = useI18n();
  const [sortField, setSortField] = useState<SortField>('finishPosition');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sorted = useMemo(() => {
    const copy = [...entries];
    copy.sort((a, b) => {
      let aVal = a[sortField] as number;
      let bVal = b[sortField] as number;
      // Put 0 / DNF at the bottom
      if (sortField === 'finishPosition') {
        if (aVal <= 0) aVal = 9999;
        if (bVal <= 0) bVal = 9999;
      }
      if (sortField === 'bestLapMs' || sortField === 'totalTimeMs') {
        if (!aVal || aVal <= 0) aVal = Infinity;
        if (!bVal || bVal <= 0) bVal = Infinity;
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return copy;
  }, [entries, sortField, sortDir]);

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="inline ml-0.5" />
      : <ChevronDown size={12} className="inline ml-0.5" />;
  }

  // Find the best lap among all entries
  const bestLapOverall = useMemo(() => {
    let best = Infinity;
    for (const e of entries) {
      if (e.bestLapMs > 0 && e.bestLapMs < best) best = e.bestLapMs;
    }
    return best === Infinity ? 0 : best;
  }, [entries]);

  function computeGap(entry: RaceEntryResult): string {
    if (entry.finishPosition === 1) return '–';
    if (entry.lapsBehindLeader && entry.lapsBehindLeader > 0) {
      return entry.lapsBehindLeader === 1 ? `+1 ${t('races.lap')}` : `+${entry.lapsBehindLeader} ${t('races.laps')}`;
    }
    if (entry.gapToLeaderMs && entry.gapToLeaderMs > 0) {
      return `+${(entry.gapToLeaderMs / 1000).toFixed(3)}`;
    }
    return '–';
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[640px] border-collapse">
        <thead>
          <tr className="divider">
            <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted cursor-pointer select-none" onClick={() => toggleSort('finishPosition')}>
              {t('races.position')}<SortIcon field="finishPosition" />
            </th>
            <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted cursor-pointer select-none" onClick={() => toggleSort('kartNumber')}>
              {t('races.kart')}<SortIcon field="kartNumber" />
            </th>
            <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.driver')}</th>
            {!compact && (
              <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted cursor-pointer select-none" onClick={() => toggleSort('gridPosition')}>
                {t('races.grid')}<SortIcon field="gridPosition" />
              </th>
            )}
            <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted cursor-pointer select-none" onClick={() => toggleSort('bestLapMs')}>
              {t('races.bestLap')}<SortIcon field="bestLapMs" />
            </th>
            {!compact && (
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted cursor-pointer select-none" onClick={() => toggleSort('totalTimeMs')}>
                {t('races.totalTime')}<SortIcon field="totalTimeMs" />
              </th>
            )}
            {showLaps && (
              <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted cursor-pointer select-none" onClick={() => toggleSort('lapsCompleted')}>
                {t('races.laps')}<SortIcon field="lapsCompleted" />
              </th>
            )}
            <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.gap')}</th>
            <th className="text-center py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('races.status')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((entry, index) => {
            const isBestLap = entry.bestLapMs > 0 && entry.bestLapMs === bestLapOverall;
            const posGain = entry.gridPosition > 0 && entry.finishPosition > 0
              ? entry.gridPosition - entry.finishPosition
              : 0;

            const isHighlighted = highlightDriverId === entry.driverId || (highlightDriverIds?.has(entry.driverId) ?? false);
            const shouldScroll = highlightDriverId === entry.driverId;

            return (
              <tr
                key={entry.driverId}
                ref={shouldScroll ? (el) => el?.scrollIntoView({ behavior: 'smooth', block: 'center' }) : undefined}
                className={`hover:bg-bg-hover transition-colors ${index < sorted.length - 1 ? 'divider' : ''} ${isHighlighted ? 'bg-purple/10 ring-1 ring-purple/30' : ''}`}
              >
                <td className="py-2.5 px-4">
                  <PositionBadge position={entry.finishPosition} size="sm" />
                </td>
                <td className="py-2.5 px-4 mono text-text-secondary font-medium">
                  {entry.kartNumber}
                </td>
                <td className="py-2.5 px-4">
                  <button
                    className="text-left text-text-primary hover:text-accent transition-colors font-medium cursor-pointer"
                    onClick={() => onDriverClick?.(entry.driverId)}
                  >
                    {entry.firstName} {entry.lastName}
                  </button>
                </td>
                {!compact && (
                  <td className="py-2.5 px-4 mono text-text-secondary text-sm">
                    P{entry.gridPosition}
                    {posGain !== 0 && (
                      <span className={`ml-1.5 text-[10px] font-semibold ${posGain > 0 ? 'text-green' : 'text-red'}`}>
                        {posGain > 0 ? `▲${posGain}` : `▼${Math.abs(posGain)}`}
                      </span>
                    )}
                  </td>
                )}
                <td className={`py-2.5 px-4 text-right mono text-sm ${isBestLap ? 'text-purple font-bold' : 'text-text-primary'}`}>
                  {formatLapTime(entry.bestLapMs)}
                </td>
                {!compact && (
                  <td className="py-2.5 px-4 text-right mono text-sm text-text-secondary">
                    {formatLapTime(entry.totalTimeMs)}
                  </td>
                )}
                {showLaps && (
                  <td className="py-2.5 px-4 text-right mono text-sm text-text-secondary">
                    {entry.lapsCompleted > 0 ? entry.lapsCompleted : '–'}
                  </td>
                )}
                <td className="py-2.5 px-4 text-right mono text-sm text-text-muted">
                  {computeGap(entry)}
                </td>
                <td className="py-2.5 px-4 text-center">
                  <StatusBadge status={entry.status} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
